import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useCart from '@hooks/useCart';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import PackageTemplate from '@ui/Package/Package';
import QuantityTemplate from '@ui/Quantity/Quantity';
import TagTemplate from '@ui/Tag/Tag';
import { formatMoneyToCZK } from '@utils/cart.utils';
import moment from 'moment';
import { FBQ_EVENTS, ROUTES } from '../../constants';
import { SUBSCRIPTION_PACKAGES } from '../../constants/packages';
import { EventDateState, IModuleItem, ITrainingItemDate } from '../../interfaces/Calendar';
import {
    Buy,
    Form,
    HasBoughtTag,
    Item,
    Label,
    ProductDate,
    ProductDetail,
    ProductMap,
    ProductName,
    ProductPackage,
    ProductPackageTitle,
    ProductPrice,
    ProductQuantity,
    Table,
    TableHeader,
    TableHeaderLabel,
    TableItem,
    Tags,
    Value,
} from './TrainingDates.elements';

export interface TrainingDatesProps {
    dates: ITrainingItemDate[];
    isCustom: boolean;
    isAcademy?: boolean;
    modules: IModuleItem[];
}

const TrainingDatesTemplate: FC<TrainingDatesProps> = ({ dates: trainingDates, isCustom, isAcademy, modules }) => {
    const { t } = useTranslations();

    const router = useRouter();

    const { addOrUpdateItemInCart } = useCart();

    const [dates, setDates] = useState([]);
    const [quantities, setQuantities] = useState({});

    useEffect(() => {
        if (trainingDates?.length > 0) {
            setDates(trainingDates);
            trainingDates.map((trainingDate) => {
                (quantities as { [key in string]: number })[trainingDate.date._key] = 1;
                setQuantities(quantities);
            });
        }
    }, [trainingDates]);

    return (
        <>
            <Table>
                <TableHeader>
                    <TableHeaderLabel width={24}>{t('cart.header.term')}</TableHeaderLabel>
                    <TableHeaderLabel width={20} center hideOnMobile>
                        {t('cart.header.count')}
                    </TableHeaderLabel>
                    <TableHeaderLabel width={15} center hideOnMobile>
                        {t('cart.header.priceWithoutVat')}
                    </TableHeaderLabel>
                    <TableHeaderLabel width={18} center hideOnMobile>
                        {t('cart.header.price')}
                    </TableHeaderLabel>
                </TableHeader>
                {dates?.map((date, index: number) => {
                    const formattedDateFromWithDay = moment(date.date.from).local().format('DD. MM. YYYY');
                    const formattedDateToWithDay = moment(date.date.to).local().format('DD. MM. YYYY');

                    const isRealized = isAcademy
                        ? moment().local().diff(date.date.to, 'minutes') > 0
                        : moment().local().diff(date.date.from, 'minutes') > 0;
                    const isRunning =
                        moment().local().diff(date.date.from, 'minutes') > 0 &&
                        moment().local().diff(date.date.to, 'minutes') < 0;

                    return (
                        <Item key={index}>
                            <TableItem isCheckout isSummary hasTags={date?.tags?.length > 0}>
                                <ProductDetail>
                                    <ProductName isBold>
                                        {date.accessible && <HasBoughtTag>{t('training.hasBought')}</HasBoughtTag>}
                                        {formattedDateFromWithDay}
                                        {date.date.to && formattedDateFromWithDay !== formattedDateToWithDay
                                            ? ` - ${formattedDateToWithDay}`
                                            : ''}{' '}
                                        {!isAcademy &&
                                            `(${moment(date.date.from).local().format('HH:mm')} - ${moment(date.date.to)
                                                .local()
                                                .format('HH:mm')})`}{' '}
                                        {!isAcademy && '|'}{' '}
                                        <Form withMarginTop={isAcademy}>
                                            {date.isOnline
                                                ? t('event.isOnline')
                                                : date.venues?.[0]?.city || date.venues?.[0]?.name}
                                            {date.isPresence
                                                ? `${date.isOnline ? ' | ' : ''}${t('event.isPresent')}`
                                                : ''}
                                        </Form>
                                    </ProductName>
                                    {date.venues?.[0]?.city && (
                                        <ProductDate>
                                            {date.venues?.[0]?.name?.replace(
                                                `${date.venues?.[0]?.street?.trim()},`,
                                                ''
                                            )}
                                            {date.venues?.[0]?.street?.trim() && `, ${date.venues?.[0]?.street}`}
                                            {date.venues?.[0]?.link && (
                                                <ProductMap href={date.venues?.[0]?.link}> (Mapa)</ProductMap>
                                            )}
                                        </ProductDate>
                                    )}
                                </ProductDetail>
                                <ProductQuantity>
                                    <Label>{t('cart.header.count')}</Label>
                                    {!isRealized && !isCustom && date.date.state === EventDateState.AVAILABLE && (
                                        <QuantityTemplate
                                            quantity={(quantities as { [key in string]: number })[date.date._key]}
                                            setPlusQuantity={() => {
                                                setQuantities({
                                                    ...quantities,
                                                    [date.date._key]: ((quantities as { [key in string]: number })[
                                                        date.date._key
                                                    ] += 1),
                                                });
                                            }}
                                            setMinusQuantity={() => {
                                                setQuantities({
                                                    ...quantities,
                                                    [date.date._key]: ((quantities as { [key in string]: number })[
                                                        date.date._key
                                                    ] -=
                                                        (quantities as { [key in string]: number })[date.date._key] > 1
                                                            ? 1
                                                            : 0),
                                                });
                                            }}
                                        />
                                    )}
                                </ProductQuantity>
                                <ProductPrice>
                                    <Label>{t('cart.header.priceWithoutVat')}</Label>
                                    {!isRealized && date.date.state === EventDateState.AVAILABLE && (
                                        <Value withHeight isPrice>
                                            {isCustom
                                                ? '0 Kč'
                                                : `${formatMoneyToCZK(
                                                      Math.round(
                                                          date.modules?.[0]?.price *
                                                              (quantities as { [key in string]: number })[
                                                                  date.date._key
                                                              ] *
                                                              100
                                                      ) / 100
                                                  )} Kč`}
                                        </Value>
                                    )}
                                </ProductPrice>
                                <ProductPrice>
                                    <Label>{t('cart.header.price')}</Label>
                                    {!isRealized && date.date.state === EventDateState.AVAILABLE && (
                                        <Value withHeight isPrice>
                                            {isCustom
                                                ? '0 Kč'
                                                : `${formatMoneyToCZK(
                                                      Math.round(
                                                          date.modules?.[0]?.price_with_vat *
                                                              (quantities as { [key in string]: number })[
                                                                  date.date._key
                                                              ] *
                                                              100
                                                      ) / 100
                                                  )} Kč`}
                                        </Value>
                                    )}
                                </ProductPrice>
                                <Buy>
                                    {!isAcademy && isRunning && (
                                        <ButtonTemplate disabled text={t('training.status.progress')} fullWidth />
                                    )}
                                    {isRealized && !isRunning && (
                                        <ButtonTemplate disabled text={t('training.status.realized')} fullWidth />
                                    )}
                                    {(isAcademy || !isRunning) && !isRealized && !isCustom && (
                                        <ButtonTemplate
                                            onClick={() => {
                                                (window as any)?.fbq?.('track', FBQ_EVENTS.ADD_TO_CART);
                                                addOrUpdateItemInCart(
                                                    date?.modules?.[0]?._key,
                                                    (quantities as { [key in string]: number })[date.date._key]
                                                );
                                            }}
                                            text={
                                                date.accessible
                                                    ? t('training.addToCartAgain.button')
                                                    : t('training.addToCart.button')
                                            }
                                            fullWidth
                                            disabled={date.date.state != EventDateState.AVAILABLE}
                                        />
                                    )}
                                </Buy>
                            </TableItem>
                            {date?.tags?.filter((tag: { label: string }) => tag.label)?.length > 0 && (
                                <Tags>
                                    {date?.tags
                                        ?.filter((tag: { label: string }) => tag.label)
                                        ?.map((tag: { title: string; color: string }, key: number) => (
                                            <TagTemplate key={key} name={tag.title} color={tag.color} />
                                        ))}
                                </Tags>
                            )}
                        </Item>
                    );
                })}
                {modules?.length > 0 && (
                    <TableItem isCheckout isSummary>
                        <ProductDetail>
                            <ProductName isBold noWrap>
                                {t('training.subscription.label')}
                                <ProductPackage>
                                    {modules.map((module, key) => {
                                        const subscriptionPackage = SUBSCRIPTION_PACKAGES.find(
                                            (subPackage) => subPackage.key === module.module._key
                                        );

                                        return (
                                            <PackageTemplate
                                                key={key}
                                                color={subscriptionPackage.color}
                                                symbol={subscriptionPackage.icon}
                                            />
                                        );
                                    })}
                                </ProductPackage>
                            </ProductName>
                            <ProductPackageTitle minWidth={300}>{t('training.subscription.title')}</ProductPackageTitle>
                        </ProductDetail>
                        <ProductQuantity></ProductQuantity>
                        <ProductPrice />
                        <ProductPrice>
                            <Label>{t('cart.header.price')}</Label>
                            <Value withHeight isPrice>
                                0 Kč
                            </Value>
                        </ProductPrice>
                        <Buy>
                            <ButtonTemplate
                                onClick={() => router.push({ pathname: ROUTES.SUBSCRIPTION })}
                                text={t('training.showMore.button')}
                                fullWidth
                            />
                        </Buy>
                    </TableItem>
                )}
            </Table>
        </>
    );
};

export default TrainingDatesTemplate;
